"use client";
import React, { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Facebook, Instagram, Linkedin, Youtube } from "lucide-react";

export default function BlogPostDetail({ params }) {
  const resolvedParams = React.use(params);
  const pathname = usePathname();
  const [activeSection, setActiveSection] = useState('');
  const [blogData, setBlogData] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [tocItems, setTocItems] = useState([]);
  const [contentHtml, setContentHtml] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await fetch("https://hyreback.hyrelancer.com/api/sitegetBlog", {
          method: "GET",
          headers: {
            "Content-Type": "application/json"
          }
        });
        const data = await response.json();
        if (data.success && data.blogs) {
          const currentBlog = data.blogs.find(b => b.blo_slug === resolvedParams.id);
          if (currentBlog) {
            setBlogData(currentBlog);
            
            // Extract toc from content
            const parser = new DOMParser();
            const doc = parser.parseFromString(currentBlog.blo_desc, "text/html");
            const headings = doc.querySelectorAll('h2');
            const newTocItems = [];
            
            headings.forEach((heading, index) => {
              const text = heading.textContent.trim();
              const id = `heading-${index}`;
              heading.setAttribute('id', id);
              // Adding style directly since Tailwind might not apply here directly easily without global css
              heading.className = "text-[#000] text-[28px] font-bold leading-tight mb-2 mt-6";
              newTocItems.push({ id, label: text });
            });

            setContentHtml(doc.body.innerHTML);
            setTocItems(newTocItems);
            if (newTocItems.length > 0) setActiveSection(newTocItems[0].id);

            // Related blogs
            const related = data.blogs
              .filter(b => b.blo_bca_id === currentBlog.blo_bca_id && b.blo_id !== currentBlog.blo_id)
              .slice(0, 3);
            setRelatedBlogs(related);
          }
        }
      } catch (error) {
        console.error("Failed to fetch blog:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [resolvedParams.id]);

  useEffect(() => {
    if (!tocItems.length) return;
    const handleScroll = () => {
      const sections = tocItems.map(item => item.id);
      let current = '';

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 200) { 
            current = section;
          }
        }
      }

      if (current) {
        setActiveSection(current);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [tocItems]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 120, // Offset for sticky headers/visual preference
        behavior: 'smooth'
      });
    }
  };

  const handleShare = (platform) => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const title = blogData?.blo_title || 'Check out this blog post';

    if (platform === 'facebook') {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
    } else if (platform === 'twitter') {
      window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank');
    } else if (platform === 'linkedin') {
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
    } else {
      // Fallback for platforms without direct URL sharing
      if (navigator.share) {
        navigator.share({ title, url }).catch(console.error);
      } else {
        navigator.clipboard.writeText(url);
        alert('Link copied to clipboard!');
      }
    }
  };

  // Generate breadcrumb from pathname
  const generateBreadcrumb = (path) => {
    if (!path) return [];

    const segments = path.split('/').filter(Boolean);
    const breadcrumbs = [{ name: 'Home', path: '/' }];

    let currentPath = '';
    segments.forEach((segment) => {
      currentPath += `/${segment}`;
      // Capitalize first letter and replace hyphens with spaces
      const name = segment
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      breadcrumbs.push({ name, path: currentPath });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumb(pathname);

  return (
    <div className="relative min-h-screen bg-white pb-20">
      {/* Hero Banner Section */}
      <div
        className="relative w-full max-w-[1920px] mx-auto h-[330px] overflow-hidden flex flex-col items-start justify-center px-8 md:px-12 lg:px-16 xl:px-20 2xl:px-[140px] box-border bg-[length:100%_100%] bg-no-repeat bg-center text-left"
        style={{ backgroundImage: "url('/images/about-us-banner.png')" }}
      >
        <div className="relative z-10 flex flex-col items-start gap-3 md:gap-[19px]">
          <div className="relative text-3xl md:text-5xl lg:text-[48px] xl:text-[52px] 2xl:text-[56px] leading-tight lg:leading-[48px] xl:leading-[52px] 2xl:leading-[56px] font-medium text-white">
            Blog
          </div>
          <div className="relative text-sm md:text-base leading-6 font-semibold text-[#f4f4f4]">
            {breadcrumbs.map((crumb, index) => (
              <span key={crumb.path}>
                {index > 0 && <span className="mx-2">|</span>}
                {index === breadcrumbs.length - 1 ? (
                  <span>{crumb.name}</span>
                ) : (
                  <Link
                    href={crumb.path}
                    className="hover:underline transition-opacity hover:opacity-80"
                  >
                    {crumb.name}
                  </Link>
                )}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Container */}
      <div className="w-full max-w-[1640px] mx-auto flex flex-col items-start pt-[60px] md:pt-[100px] px-6 md:px-12 lg:px-16 xl:px-[144px] bg-white">
        
        {/* Top Header Section */}
        <div className="w-full flex flex-col items-start gap-[16px] mb-8">
          <div className="flex flex-col items-start gap-[16px]">
            {/* Category Tag */}
            <div className="flex justify-center items-center px-[8px] py-[8px] gap-[11.667px] rounded-[48px] bg-[#F4F4F4]">
              <span className="text-[#000] font-['Roboto',sans-serif] text-[14px] leading-[24px] font-normal">
                {blogData ? (blogData.blog_category?.bca_title || "Uncategorized") : "Loading..."}
              </span>
            </div>
            {/* Date and Time */}
            <span className="text-[#888] font-['Roboto',sans-serif] text-[14px] leading-[24px] font-normal">
              {blogData ? new Date(blogData.blo_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) + " . 3 Mins" : ""}
            </span>
          </div>

          {/* Heading */}
          <h1 className="text-[#000] font-['Roboto',sans-serif] text-[36px] md:text-[56px] font-medium leading-tight md:leading-[56px] m-0 max-w-[1200px]">
            {blogData ? blogData.blo_title : "Loading..."}
          </h1>
        </div>

        {/* Main Image */}
        {blogData && (
          <div 
            className="w-full h-[300px] md:h-[564px] rounded-[16px] bg-lightgray bg-center bg-cover bg-no-repeat mb-16"
            style={{ backgroundImage: `url('https://hyreback.hyrelancer.com/${blogData.blo_img}')` }}
          ></div>
        )}

        {/* Content Section */}
        <div className="w-full flex flex-col lg:flex-row items-start gap-12 lg:gap-[120px]">
          {/* Left Sidebar - Table of Contents & Social Share */}
          <div className="w-full lg:w-[300px] flex-shrink-0 flex flex-col gap-12 relative lg:sticky lg:top-[100px] z-10 bg-white">
            {/* TOC */}
            <div className="flex flex-col gap-4 text-[16px] font-['Roboto',sans-serif]">
              {tocItems.map((item) => (
                <div 
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`flex items-center font-semibold pl-4 cursor-pointer transition-all duration-300 ${activeSection === item.id ? 'text-[#3A599C] border-l-[3px] border-[#3A599C]' : 'text-gray-700 border-l-[3px] border-transparent hover:text-[#3A599C]'}`}
                >
                  {item.label}
                </div>
              ))}
            </div>

            {/* Social Share */}
            <div className="flex items-center gap-4 mt-4">
              <span className="text-[#3A599C] font-semibold text-[16px]">Share</span>
              <div className="flex items-center gap-3">
                <button onClick={() => handleShare('facebook')} className="w-[44px] h-[44px] rounded-full border border-[#3A599C] flex items-center justify-center text-[#3A599C] hover:bg-[#3A599C] hover:text-white transition-colors">
                  <Facebook size={20} />
                </button>
                <button onClick={() => handleShare('instagram')} className="w-[44px] h-[44px] rounded-full border border-[#3A599C] flex items-center justify-center text-[#3A599C] hover:bg-[#3A599C] hover:text-white transition-colors">
                  <Instagram size={20} />
                </button>
                <button onClick={() => handleShare('twitter')} className="w-[44px] h-[44px] rounded-full border border-[#3A599C] flex items-center justify-center text-[#3A599C] hover:bg-[#3A599C] hover:text-white transition-colors">
                  <span className="font-bold text-[20px]">X</span>
                </button>
                <button onClick={() => handleShare('linkedin')} className="w-[44px] h-[44px] rounded-full border border-[#3A599C] flex items-center justify-center text-[#3A599C] hover:bg-[#3A599C] hover:text-white transition-colors">
                  <Linkedin size={20} />
                </button>
                <button onClick={() => handleShare('youtube')} className="w-[44px] h-[44px] rounded-full border border-[#3A599C] flex items-center justify-center text-[#3A599C] hover:bg-[#3A599C] hover:text-white transition-colors">
                  <Youtube size={20} />
                </button>
              </div>
            </div>
          </div>

          {/* Right Content Area */}
          <div className="flex-1 flex flex-col gap-6 font-['Roboto',sans-serif] text-[16px] leading-[24px] text-black font-normal blog-content">
            <style dangerouslySetInnerHTML={{__html: `
              .blog-content p {
                margin-bottom: 1rem;
              }
              .blog-content ul {
                list-style-type: disc;
                padding-left: 1.25rem;
                display: flex;
                flex-direction: column;
                gap: 0.25rem;
                margin-bottom: 1rem;
              }
              .blog-content strong {
                font-weight: bold;
              }
            `}} />
            {loading ? (
              <p>Loading content...</p>
            ) : contentHtml ? (
              <div dangerouslySetInnerHTML={{ __html: contentHtml }} />
            ) : (
              <p>No content available.</p>
            )}
          </div>
        </div>
      </div>
      
      {/* Related Blogs Section */}
      <div className="w-full max-w-[1920px] mx-auto flex flex-col items-start py-[80px] px-6 md:px-12 lg:px-16 xl:px-[144px] gap-[40px] bg-white">
        <h2 className="text-[#000] text-[36px] font-medium font-['Roboto',sans-serif]">Related blogs</h2>
        <div className="w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-[32px]">
          {/* Dynamic Related Cards */}
          {relatedBlogs.map((post) => (
            <Link href={`/blog/${post.blo_slug}`} key={post.blo_id} className="w-full max-w-[523px] flex flex-col p-4 md:p-[24px] rounded-[16px] gap-[25px] border border-[rgba(0,0,0,0.26)] bg-[rgba(255,255,255,0.16)] backdrop-blur-[14.1px] mx-auto group cursor-pointer transition-all">
              <div 
                className="w-full aspect-[475/338] rounded-[16px] bg-lightgray bg-center bg-cover bg-no-repeat relative flex items-center justify-center overflow-hidden"
                style={{ backgroundImage: `url('https://hyreback.hyrelancer.com/${post.blo_img}')`, backgroundColor: 'lightgray' }}
              >
                <div className="absolute inset-0 bg-black/10 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <div className="w-full flex flex-col items-start gap-[12px]">
                <div className="flex justify-center items-center py-[8px] px-[16px] bg-[#F4F4F4] rounded-[48px]">
                  <span className="text-[#000] font-['Roboto',sans-serif] text-[14px] leading-[24px] font-normal">
                    {post.blog_category?.bca_title || "Uncategorized"}
                  </span>
                </div>
                <span className="text-[#888] font-['Roboto',sans-serif] text-[14px] leading-[24px] font-normal">
                  {new Date(post.blo_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} . 3 Mins
                </span>
                <h3 className="w-full text-[#000] font-['Roboto',sans-serif] text-[20px] md:text-[24px] leading-[28px] md:leading-[24px] font-semibold m-0">
                  {post.blo_title}
                </h3>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
